import { useUserState } from '../../redux/reducers/userReducer';
import { useAccountState } from '../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router';
import { FormikProps, useFormik } from 'formik';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    Label,
    Persona,
    SelectTabData,
    SelectTabEvent,
    Switch,
    Tab,
    TabList,
    Text,
    Textarea,
    Toolbar,
    ToolbarDivider,
    ToggleButton,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { DatePicker, DatePickerProps } from '@fluentui/react-datepicker-compat';
import type { TimePickerProps } from '@fluentui/react-timepicker-compat';
import { TimePicker, formatDateToTimeString } from '@fluentui/react-timepicker-compat';
import { addYears, calendarDayGridClassNames } from '@fluentui/react-calendar-compat';
import {
    ArrowResetRegular,
    ArrowUpload16Regular,
    bundleIcon,
    CalendarAddRegular,
    CalendarEditRegular,
    DeleteRegular,
    InfoRegular,
    MoneyFilled,
    MoneyRegular,
} from '@fluentui/react-icons';
import { Column, Paths, Row } from '../common';
import { CaseDocument, CaseEvent, CaseMilestone, Note, useInitialValues, User, UserRef } from '../../data/types';
import { useCaseDocumentService, useCaseMilestoneService, useCaseNoteService, useCaseService } from '../../data/services';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { useAccess } from '../../auth/useAccess';
import { CaseNoteForm } from '../controls/CaseNoteForm';
import { NoteControl } from '../controls/NoteControl';
import { useUtilities } from '../../utilities/useUtilities';
import { useValidation } from '../../data/types/validation';
import { useDialogControls } from './useDialogControls';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { EditableLabel } from '../controls/EditableLabel';
import { appTokens } from '../common/Styles/appTokens';
import { Section } from '../common/Section';
import { ConfirmationDialog } from './ConfirmationDialog';
import { UserDropdown } from '../controls/Comboboxes/UserDropdown';
import { PersonControl } from '../controls/PersonControl';
import { DocumentTable } from '../pages/Cases/CaseDocuments/DocumentTable';
import { DocumentDrawer } from '../pages/Cases/CaseDocuments/DocumentDrawer';
import FileUploadManager from '../../data/services/FileUploadManager';
import { useToasts } from '../toasts/useToasts';
import { useCaseDocumentActions } from '../../redux/actions/caseDocumentActions';
import { useCustomDataTableFeatures } from '../controls/CustomDataTable/useCustomDataTableFeatures';

const useStyles = makeStyles({
    dialog: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
        maxWidth: '75vw',
        height: '75vh',
    },
    dialogLaptopScreen: {
        maxWidth: '80vw',
    },
    dialogUnderLaptopScreen: {
        maxWidth: '85vw',
    },
    casePersona: {
        userSelect: 'none',
        cursor: 'pointer',
    },
    title: {
        fontSize: '1.25rem',
    },
    billableBtn: {
        ':disabled': {
            //border: '1px solid green'
            color: '#333',
        },
    },
    dateCalendar: {
        [`& .${calendarDayGridClassNames.weekRow}`]: {
            backgroundColor: tokens.colorNeutralBackground1,
        },
        [`& .${calendarDayGridClassNames.weekDayLabelCell}`]: {
            backgroundColor: tokens.colorNeutralBackground1,
        },
    },
    documentsContentArea: {
        position: 'relative',
        minHeight: 0,
    },
    documentsDragArea: {
        position: 'absolute',
        inset: '.5rem',
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorBrandBackground2,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        opacity: '0',
    },
    documentsDragActive: {
        border: `3px solid ${tokens.colorBrandBackground4Static}`,
        opacity: '.4',
        zIndex: tokens.zIndexOverlay,
    },
    documentsBtn: {
        marginInline: '.25rem',
    },
    documentsUploadBtn: {
        marginBlock: '.25rem',
    },
    documentsDrawer: {
        width: '32rem',
    },
});

enum EventDialogTabs {
    details = 'Details',
    discussion = 'Discussion',
    documents = 'Documents',
}

const MAX_FILES_PER_DROP = 20;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 * 1024; // 50 GB
const ACCEPTED_DOCUMENT_TYPES: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'text/plain': [
        '.txt',
        '.log',
        '.md',
        '.markdown',
        '.rtf',
        '.ini',
        '.cfg',
        '.yaml',
        '.yml',
        '.xml',
        '.html',
        '.htm',
        '.css',
        '.js',
        '.ts',
        '.tsx',
        '.sql',
    ],
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff'],
};

export type AddEditCaseEventProps = {
    caseId: string;
    onSubmit?: (values: CaseEvent) => Promise<void>;
    eventToEdit?: CaseEvent;
    children?: JSX.Element;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    milestone?: CaseMilestone;
};

export const AddEditCaseEventDialog = forwardRef((props: AddEditCaseEventProps, _ref) => {
    const { children, onSubmit, eventToEdit, caseId, open, onOpenChange, milestone } = props;

    // Hooks
    const { primaryAccount } = useAccountState();
    const { users } = useUserState();
    const access = useAccess(primaryAccount?.user);
    const initValues = useInitialValues();
    const caseService = useCaseService();
    const eventCaseId = eventToEdit?.caseId || caseId;
    const caseObj = caseService.findCaseById(eventCaseId as string);
    const caseNoteService = useCaseNoteService(eventCaseId);
    const milestoneService = useCaseMilestoneService(eventCaseId as string);
    const utils = useUtilities();
    const navTo = useNavigate();
    const mediaSizes = useMediaSizes();
    const { validateCaseEvent } = useValidation();

    // State
    const [milestones, setMilestones] = useState<CaseMilestone[]>([]);
    const [notesToAddOnSave, setNotesToAddOnSave] = useState<Note[]>([]);
    const [loadedEventNotes, setLoadedEventNotes] = useState<Note[]>([]);
    const [tab, setTab] = useState<EventDialogTabs>(EventDialogTabs.details);
    const [dateMin, setDateMin] = useState<Date>(
        !!caseObj?.retainedDate ? new Date(caseObj.retainedDate) : addYears(new Date(), -1),
    );

    // Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const dialogSurfaceClasses = mergeClasses(
        styles.dialog,
        mediaSizes.isUnderDesktop && styles.dialogLaptopScreen,
        mediaSizes.isUnderLaptop && styles.dialogUnderLaptopScreen,
    );
    const billableBtnClasses = mergeClasses(commonStyles.button.root, styles.billableBtn);

    // Dialog controls
    const onCloseDialog = () => {
        eventForm.resetForm({ values: initEvent() });
        setNotesToAddOnSave([]);
        setTab(EventDialogTabs.details);
    };
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open, onOpenChange, onCloseDialog);

    // Form
    const initEvent = useCallback((): CaseEvent => {
        if (eventToEdit) {
            return {
                ...initValues.caseEvent(caseId),
                ...eventToEdit,
                date: utils.date.validDate(eventToEdit.date),
            };
        }

        return {
            ...initValues.caseEvent(caseId),
            milestoneId: milestone?.milestoneId,
        };
    }, [caseId, eventToEdit, initValues, milestone?.milestoneId, utils]);

    const eventForm = useFormik<CaseEvent>({
        initialValues: initEvent(),
        onSubmit: async (values, { resetForm }) => {
            const event: CaseEvent =
                typeof structuredClone === 'function'
                    ? (structuredClone(values) as CaseEvent)
                    : ({ ...values } as CaseEvent);

            if (milestone) {
                event.milestoneId = milestone.milestoneId;
            }
            const saveOperations: Promise<unknown>[] = [];

            if (notesToAddOnSave.length > 0) {
                const updatedNotes = notesToAddOnSave.map((note) => ({
                    ...note,
                    caseId: event.caseId,
                    eventId: event.eventId,
                }));
                saveOperations.push(caseNoteService.upsertCaseNotesAsync(updatedNotes));
            }

            if (onSubmit && (eventForm.dirty || notesToAddOnSave.length > 0)) {
                saveOperations.push(onSubmit(event));
            }

            closeDialog();
            await Promise.all(saveOperations);

            if (eventToEdit) {
                resetForm({ values });
            } else {
                resetForm({ values: initEvent() });
            }
        },
        onReset: () => {
            setNotesToAddOnSave([]);
            setTab(EventDialogTabs.details);
            setDateMin(!!caseObj?.retainedDate ? new Date(caseObj.retainedDate) : addYears(new Date(), -1));
        },
        validate: (values) => {
            const errors: Record<string, string> = {};

            if (values.date && !utils.date.validDate(values.date)) {
                errors.date = 'Date is invalid';
            }
            if (values.date && values.date < dateMin) {
                errors.date = 'Date cannot be earlier than the retained date of the case.';
            }

            return errors;
        },
        validateOnBlur: false,
        validateOnChange: true,
        validationSchema: validateCaseEvent.schema,
    });
    const documentService = useCaseDocumentService(eventForm.values.caseId || '');

    // Access
    const canEditEvent = () => {
        return !!eventToEdit && access.cases.canEditEvents(eventCaseId);
    };
    const canCreateEvent = () => {
        return !eventToEdit && access.cases.canAddEvents(eventCaseId);
    };
    const canAddNotes = () => {
        return !!access.cases.canAddNotes(eventCaseId);
    };
    const canEditDocs = () => {
        return !!access.cases.canEditDocuments(eventForm.values.caseId);
    };
    const canDeleteDocs = () => {
        return !!access.cases.canDeleteDocuments(eventForm.values.caseId);
    };
    const canUploadDocs = () => {
        return !!access.cases.canUploadDocuments(eventForm.values.caseId);
    };

    // Logic
    const getDialogTitle = () => {
        if (!eventToEdit) {
            return 'Add Event';
        }

        return `${access.cases.canEditEvents(eventCaseId) ? 'View / Edit' : 'View'} Event`;
    };
    const getUser = (userId: string) => {
        return users.find((u) => u.userId === userId);
    };
    const getMilestones = async (msCaseId: string) => {
        const cached = milestoneService.milestones
            .filter((ms) => ms.caseId === msCaseId)
            .sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));
        if (cached.length) return cached;

        return milestoneService.getCaseMilestonesAsync(msCaseId);
    };
    const getMilestone = (msId: string) => {
        return milestones.find((ms) => ms.milestoneId === msId);
    };

    //Notes
    const loadNotes = useCallback(async () => {
        if (!!eventToEdit && !!eventToEdit.eventId) {
            try {
                const evtNotes = await caseNoteService.getCaseNotesForEventAsync(eventToEdit.eventId);
                setLoadedEventNotes(evtNotes);
            } catch {
                // ignore note load failures and allow dialog usage
            }
        }
    }, [caseNoteService, eventToEdit]);
    const getNotes = () => {
        const existingNotes = [...(caseNoteService.notes || loadedEventNotes)].filter(
            (note) => note.eventId === eventForm.values.eventId,
        );
        return [...existingNotes, ...notesToAddOnSave];
    };
    const onSubmitNote = async (note: Note) => {
        setNotesToAddOnSave((prev) => [...prev, note]);
    };

    // UI events
    const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
        setTab(data.value as EventDialogTabs);
    };
    const onSelectDate = (date: Date | null | undefined) => {
        eventForm.setFieldValue('date', date || undefined);
    };
    const clickSave = async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        await eventForm.submitForm();
    };
    const clickCancel = (e?: React.MouseEvent<Element>) => {
        e?.stopPropagation();
        eventForm.resetForm({ values: initEvent() });
        setNotesToAddOnSave([]);
        closeDialog();
    };
    const clickCaseLink = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        if (eventForm.values.caseId) {
            navTo(Paths.viewEditCase(eventForm.values.caseId));
        }
    };
    const onUserSelect = (selUser?: User | UserRef) => {
        eventForm.setFieldValue('ownerId', selUser?.userId);
    };
    const clickBillableButton = (e: any) => {
        e.stopPropagation();

        eventForm.setFieldValue('billable', !eventForm.values.billable);
    };

    //Icons
    const MoneyIcons = bundleIcon(MoneyFilled, MoneyRegular);

    // Effects
    useEffect(() => {
        if (!dialogOpen) {
            return;
        }

        eventForm.resetForm({ values: initEvent() });

        if (eventToEdit) {
            loadNotes();
        }
    }, [dialogOpen]);

    useEffect(() => {
        if (!dialogOpen || !eventForm.values.caseId) {
            return;
        }

        documentService.loadDocumentsAsync().catch(() => {
            // Keep dialog usable even when docs fail to load.
        });
    }, [dialogOpen, eventForm.values.caseId]);

    useEffect(() => {
        setDateMin(!!caseObj?.retainedDate ? new Date(caseObj.retainedDate) : addYears(new Date(), -1));
    }, [caseObj?.retainedDate]);

    useEffect(() => {
        if (!dialogOpen) return;

        if (!!eventForm.values.caseId) {
            getMilestones(eventForm.values.caseId)?.then((ms) => setMilestones(ms));
        } else {
            setMilestones([]);
        }
    }, [dialogOpen, eventForm.values.caseId, milestone?.milestoneId]);

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface
                className={dialogSurfaceClasses}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <DialogBody style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <DialogTitle>
                        <Column noGap>
                            <Row>
                                <Column gap='small'>
                                    <Row gap='small'>
                                        <Column width='auto' vAlign='middle'>
                                            {eventToEdit ? <CalendarEditRegular /> : <CalendarAddRegular />}
                                        </Column>
                                        <Column>
                                            <Text size={300}>{getDialogTitle()}</Text>
                                        </Column>
                                    </Row>
                                    <Field validationMessage={eventForm.errors.event}>
                                        <EditableLabel
                                            labelClassName={styles.title}
                                            name='event'
                                            value={eventForm.values.event || ''}
                                            onChange={eventForm.handleChange}
                                            placeholder='Enter Event Title'
                                        />
                                    </Field>
                                </Column>
                                <Column width='auto' gap='medium' align='right' vAlign='top' padding='pr-2'>
                                    {!!eventForm.values.caseId && (
                                        <Persona
                                            onClick={clickCaseLink}
                                            className={styles.casePersona}
                                            name={caseService.findCaseById(eventForm.values.caseId)?.caseName}
                                            size='extra-small'
                                            textAlignment='center'
                                            avatar={{ color: 'colorful', shape: 'square' }}
                                            data-testid='event-dialog-case-persona'
                                        />
                                    )}
                                    {!!eventForm.values.milestoneId && (
                                        <Text weight='semibold'>
                                            Milestone: <Text>{getMilestone(eventForm.values.milestoneId)?.name}</Text>
                                        </Text>
                                    )}
                                </Column>
                            </Row>
                            <Row padding='pt-2'>
                                <Column vAlign='middle'>
                                    <Row>
                                        <Column width='auto' vAlign='middle'>
                                            <Text size={300} weight='semibold'>
                                                Owner:
                                            </Text>
                                        </Column>
                                        <Column vAlign='middle'>
                                            {canCreateEvent() || canEditEvent() ? (
                                                <Column align='left' width='auto'>
                                                    <Field required validationMessage={eventForm.errors.ownerId}>
                                                        <UserDropdown
                                                            initialUserId={eventToEdit?.ownerId || ''}
                                                            caseId={eventForm.values.caseId}
                                                            onUserSelected={onUserSelect}
                                                            appearance='outline'
                                                        />
                                                    </Field>
                                                </Column>
                                            ) : (
                                                <>
                                                    {eventToEdit?.ownerId && (
                                                        <PersonControl person={getUser(eventToEdit?.ownerId || '')} size='option' />
                                                    )}
                                                </>
                                            )}
                                        </Column>
                                    </Row>
                                </Column>
                                <Column align='right' vAlign='middle'>
                                    <ToggleButton
                                        className={billableBtnClasses}
                                        name='billable'
                                        appearance={eventForm.values.billable ? 'primary' : 'secondary'}
                                        checked={eventForm.values.billable}
                                        onChange={eventForm.handleChange}
                                        onClick={clickBillableButton}
                                        icon={<MoneyIcons />}
                                        size='small'
                                        disabled={!canEditEvent() && !canCreateEvent()}
                                        data-testid='task-dialog-billable-button'
                                    >
                                        Billable
                                        <Checkbox
                                            name='billable'
                                            className={commonStyles.button.root}
                                            labelPosition='before'
                                            checked={eventForm.values.billable}
                                            onChange={eventForm.handleChange}
                                            readOnly={!canEditEvent() && !canCreateEvent()}
                                        />
                                    </ToggleButton>
                                </Column>
                            </Row>
                        </Column>
                    </DialogTitle>
                    <DialogContent style={{ height: '100%' }}>
                        <Column flex='1' gap='small' padding='pr-2' overflowY='clip'>
                            <Column
                                height='auto'
                                padding='pb-2'
                                style={{ borderBlockEnd: `1px solid ${appTokens.text.colors.subtle}` }}
                            >
                                <TabList selectedValue={tab} onTabSelect={onTabSelect}>
                                    <Tab value={EventDialogTabs.details}>Details</Tab>
                                    <Tab value={EventDialogTabs.discussion}>Discussion</Tab>
                                    <Tab value={EventDialogTabs.documents}>Documents</Tab>
                                </TabList>
                            </Column>

                            {tab === EventDialogTabs.details && (
                                <DetailsTab
                                    eventForm={eventForm}
                                    canCreateEvent={canCreateEvent()}
                                    canEditEvent={canEditEvent()}
                                    dateMin={dateMin}
                                />
                            )}

                            {tab === EventDialogTabs.discussion && (
                                <NotesTab
                                    caseId={eventForm.values.caseId || ''}
                                    canAddNotes={canAddNotes()}
                                    notes={getNotes()}
                                    onSubmitNote={onSubmitNote}
                                />
                            )}

                            {tab === EventDialogTabs.documents && (
                                <DocumentsTab
                                    caseId={eventForm.values.caseId}
                                    eventObj={eventForm.values}
                                    accountId={primaryAccount?.account.accountId}
                                    docs={documentService.docs || []}
                                    canEditDocs={canEditDocs()}
                                    canDeleteDocs={canDeleteDocs()}
                                    canUploadDocs={canUploadDocs()}
                                    onDeleteDocs={documentService.deleteCaseDocumentsAsync}
                                    onModifyDocument={documentService.upsertCaseDocumentAsync}
                                />
                            )}
                        </Column>
                    </DialogContent>
                    <DialogActions fluid style={{ justifyContent: 'flex-end' }}>
                        {(canCreateEvent() || canEditEvent()) && (
                            <Button
                                className={commonStyles.button.root}
                                type='submit'
                                appearance='primary'
                                disabled={
                                    eventForm.isSubmitting ||
                                    !eventForm.isValid ||
                                    (!canEditEvent() && !canCreateEvent()) ||
                                    (!eventForm.dirty && notesToAddOnSave.length === 0)
                                }
                                onClick={clickSave}
                                data-testid='event-save-button'
                            >
                                Save
                            </Button>
                        )}
                        <DialogTrigger disableButtonEnhancement>
                            {!!eventForm.dirty || notesToAddOnSave.length > 0 ? (
                                <ConfirmationDialog
                                    icon={<ArrowResetRegular />}
                                    title='Discard changes?'
                                    text='You have unsaved changes. Are you sure you want to discard them?'
                                    onConfirm={clickCancel}
                                >
                                    <Button
                                        appearance='outline'
                                        className={commonStyles.button.root}
                                        data-testid='event-cancel-button'
                                    >
                                        Cancel
                                    </Button>
                                </ConfirmationDialog>
                            ) : (
                                <Button
                                    appearance='secondary'
                                    className={commonStyles.button.root}
                                    onClick={clickCancel}
                                    data-testid='event-cancel-button'
                                >
                                    Close
                                </Button>
                            )}
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
});

type DetailsTabProps = {
    eventForm: FormikProps<CaseEvent>;
    canCreateEvent?: boolean;
    canEditEvent?: boolean;
    dateMin?: Date;
};

const DetailsTab = ({ eventForm, canCreateEvent, canEditEvent, dateMin }: DetailsTabProps) => {
    const { validateCaseEvent } = useValidation();
    const canEdit = !!canCreateEvent || !!canEditEvent;
    const toValidDate = (value: unknown): Date | undefined => {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? undefined : value;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? undefined : parsed;
        }

        return undefined;
    };
    const startDateValue = toValidDate(eventForm.values.date);
    const endDateValue = toValidDate(eventForm.values.endDateTime) ?? startDateValue;

    //State
    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
    const [selectedStartTime, setSelectedStartTime] = useState<Date | null>(null);
    const [startTimePickerValue, setStartTimePickerValue] = useState<string>(
        selectedStartTime ? formatDateToTimeString(selectedStartTime) : '',
    );
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
    const [selectedEndTime, setSelectedEndTime] = useState<Date | null>(null);
    const [endTimePickerValue, setEndTimePickerValue] = useState<string>(
        selectedEndTime ? formatDateToTimeString(selectedEndTime) : '',
    );

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Start DateTime
    const mergeStartDateTime = (datePart?: Date | null, timePart?: Date | null) => {
        if (!datePart) {
            return undefined;
        }

        const nextTime = timePart ?? selectedStartTime ?? startDateValue;

        return new Date(
            datePart.getFullYear(),
            datePart.getMonth(),
            datePart.getDate(),
            nextTime?.getHours() ?? 0,
            nextTime?.getMinutes() ?? 0,
            nextTime?.getSeconds() ?? 0,
            nextTime?.getMilliseconds() ?? 0,
        );
    };
    const onSelectStartDate: DatePickerProps['onSelectDate'] = (date) => {
        setSelectedStartDate(date ?? null);
        eventForm.setFieldValue('date', mergeStartDateTime(date ?? null));
    };
    const onStartTimeChange: TimePickerProps['onTimeChange'] = (_ev, data) => {
        setSelectedStartTime(data.selectedTime ?? null);
        setStartTimePickerValue(data.selectedTimeText ?? '');

        const datePart = selectedStartDate ?? startDateValue ?? null;
        eventForm.setFieldValue('date', mergeStartDateTime(datePart, data.selectedTime ?? null));
    };
    const onStartTimePickerInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setStartTimePickerValue(ev.target.value);
    };

    //End DateTime
    const mergeEndDateTime = (datePart?: Date | null, timePart?: Date | null) => {
        if (!datePart) {
            return undefined;
        }

        const nextTime = timePart ?? selectedEndTime ?? endDateValue ?? startDateValue;

        return new Date(
            datePart.getFullYear(),
            datePart.getMonth(),
            datePart.getDate(),
            nextTime?.getHours() ?? 0,
            nextTime?.getMinutes() ?? 0,
            nextTime?.getSeconds() ?? 0,
            nextTime?.getMilliseconds() ?? 0,
        );
    };
    const onSelectEndDate: DatePickerProps['onSelectDate'] = (date) => {
        setSelectedEndDate(date ?? null);
        eventForm.setFieldValue('endDateTime', mergeEndDateTime(date ?? null));
    };
    const onEndTimeChange: TimePickerProps['onTimeChange'] = (_ev, data) => {
        setSelectedEndTime(data.selectedTime ?? null);
        setEndTimePickerValue(data.selectedTimeText ?? '');

        const datePart = selectedEndDate ?? endDateValue ?? startDateValue ?? null;
        eventForm.setFieldValue('endDateTime', mergeEndDateTime(datePart, data.selectedTime ?? null));
    };
    const onEndTimePickerInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setEndTimePickerValue(ev.target.value);
    };

    useEffect(() => {
        const startDateTime = startDateValue;

        if (!startDateTime) {
            setSelectedStartDate(null);
            setSelectedStartTime(null);
            setStartTimePickerValue('');
            return;
        }

        setSelectedStartDate(startDateTime);
        setSelectedStartTime(startDateTime);
        setStartTimePickerValue(formatDateToTimeString(startDateTime));
    }, [startDateValue]);

    useEffect(() => {
        const endDateTime = endDateValue;

        if (!endDateTime) {
            setSelectedEndDate(null);
            setSelectedEndTime(null);
            setEndTimePickerValue('');
            return;
        }

        setSelectedEndDate(endDateTime);
        setSelectedEndTime(endDateTime);
        setEndTimePickerValue(formatDateToTimeString(endDateTime));
    }, [endDateValue]);

    return (
        <Column flex='1' height='100%' padding='py-2' scroll>
            <Row>
                <Column flex='2'>
                    <Section title='Details'>
                        <Field
                            label='Description'
                            validationMessage={eventForm.errors.description}
                            hint={`${eventForm.values.description?.length || 0} / ${validateCaseEvent.description.maxLength}`}
                        >
                            <Textarea
                                name='description'
                                rows={5}
                                value={eventForm.values.description || ''}
                                onChange={eventForm.handleChange}
                                readOnly={!canEdit}
                                appearance={!canEdit ? 'outline' : undefined}
                                maxLength={validateCaseEvent.description.maxLength}
                                data-testid='event-description-textarea'
                            />
                        </Field>

                        <Row>
                            <Column>
                                <Field label='Location'>
                                    <Input
                                        name='location'
                                        value={eventForm.values.location || ''}
                                        onChange={eventForm.handleChange}
                                        readOnly={!canEdit}
                                        appearance={!canEdit ? 'outline' : undefined}
                                        data-testid='event-location-input'
                                    />
                                </Field>
                                <Field label='Event Type'>
                                    <Input
                                        name='eventType'
                                        value={eventForm.values.eventType || ''}
                                        onChange={eventForm.handleChange}
                                        readOnly={!canEdit}
                                        appearance={!canEdit ? 'outline' : undefined}
                                        data-testid='event-type-input'
                                    />
                                </Field>
                            </Column>
                            <Column></Column>
                        </Row>
                    </Section>
                </Column>
                <Column flex='1'>
                    <Section title='Schedule'>
                        <Row>
                            <Checkbox
                                name='due'
                                checked={!!eventForm.values.due}
                                onChange={(_, data) => eventForm.setFieldValue('due', !!data.checked)}
                                disabled={!canEdit}
                                label='Is Due Date?'
                                data-testid='event-due-checkbox'
                            />
                            {!eventForm.values.due && (
                                <Checkbox
                                    name='allDay'
                                    checked={!!eventForm.values.allDay}
                                    onChange={(_, data) => eventForm.setFieldValue('allDay', !!data.checked)}
                                    disabled={!canEdit}
                                    label='All Day Event'
                                    data-testid='event-all-day-checkbox'
                                />
                            )}
                        </Row>

                        <Field
                            label={`${eventForm.values.due ? 'Due' : 'Start'} Date`}
                            required
                            validationMessage={eventForm.errors.date}
                        >
                            <Row gap='small' noWrap justifyContent='space-between'>
                                <Column width='auto'>
                                    <DatePicker
                                        name='startDateTime'
                                        calendar={{ className: styles.dateCalendar }}
                                        value={startDateValue}
                                        onSelectDate={onSelectStartDate}
                                        placeholder='Select a date...'
                                        minDate={dateMin}
                                        disabled={!canEdit}
                                        data-testid='event-date-picker'
                                    />
                                </Column>
                                {!eventForm.values.allDay && (
                                    <Column width='auto' align='left'>
                                        <TimePicker
                                            style={{ minWidth: '90px' }}
                                            listbox={{ className: commonStyles.scroll.classes.scrollbarClasses }}
                                            placeholder='Select a time...'
                                            freeform
                                            dateAnchor={startDateValue}
                                            selectedTime={selectedStartTime}
                                            onTimeChange={onStartTimeChange}
                                            value={startTimePickerValue}
                                            onInput={onStartTimePickerInput}
                                        />
                                    </Column>
                                )}
                            </Row>
                        </Field>

                        {!eventForm.values.due && (
                            <Field label='End Date' required validationMessage={eventForm.errors.endDateTime}>
                                <Row gap='small' noWrap justifyContent='space-between'>
                                    <Column width='auto'>
                                        <DatePicker
                                            name='endDateTime'
                                            calendar={{ className: styles.dateCalendar }}
                                            value={endDateValue}
                                            onSelectDate={onSelectEndDate}
                                            placeholder='Select a date...'
                                            minDate={startDateValue}
                                            disabled={!canEdit}
                                            data-testid='event-date-picker'
                                        />
                                    </Column>
                                    {!eventForm.values.allDay && (
                                        <Column width='auto' align='left'>
                                            <TimePicker
                                                style={{ minWidth: '90px' }}
                                                listbox={{ className: commonStyles.scroll.classes.scrollbarClasses }}
                                                placeholder='Select a time...'
                                                freeform
                                                dateAnchor={endDateValue}
                                                selectedTime={selectedEndTime || selectedStartTime}
                                                onTimeChange={onEndTimeChange}
                                                value={endTimePickerValue || startTimePickerValue}
                                                onInput={onEndTimePickerInput}
                                            />
                                        </Column>
                                    )}
                                </Row>
                            </Field>
                        )}
                    </Section>

                    <Section title='Planning'>
                        <Field label='Priority'>
                            <Input
                                name='priority'
                                value={eventForm.values.priority || ''}
                                onChange={eventForm.handleChange}
                                readOnly={!canEdit}
                                appearance={!canEdit ? 'outline' : undefined}
                                data-testid='event-priority-input'
                            />
                        </Field>
                        <Checkbox
                            name='followUpRequired'
                            checked={!!eventForm.values.followUpRequired}
                            onChange={(_, data) => eventForm.setFieldValue('followUpRequired', !!data.checked)}
                            disabled={!canEdit}
                            label='Follow Up Required'
                            data-testid='event-followup-checkbox'
                        />
                    </Section>
                </Column>
            </Row>
        </Column>
    );
};

type NotesTabProps = {
    caseId: string;
    canAddNotes?: boolean;
    notes: Note[];
    onSubmitNote?: (note: Note) => Promise<void>;
};

const NotesTab = ({ caseId, canAddNotes, notes, onSubmitNote }: NotesTabProps) => {
    const sortNotes = (a: Note, b: Note) => {
        const aDate = new Date(a.lifecycle?.createdOn || '').getTime();
        const bDate = new Date(b.lifecycle?.createdOn || '').getTime();
        return bDate - aDate;
    };

    return (
        <Column flex='1' gap='medium' padding='px-3'>
            {!!canAddNotes && !!caseId && (
                <Column width='50%'>
                    <CaseNoteForm caseId={caseId} submitNote={onSubmitNote} title='Add Comment' />
                </Column>
            )}
            <Column flex='1' gap='small'>
                <Text weight='semibold'>Discussion</Text>
                <Column flex='1' gap='medium' padding='py-1' maxHeight='calc(75vh - 25rem)' scroll>
                    {notes?.sort(sortNotes).map((note: Note) => (
                        <NoteControl key={note.noteId} note={note} />
                    ))}
                </Column>
            </Column>
        </Column>
    );
};

type DocumentsTabProps = {
    caseId?: string;
    eventObj: CaseEvent;
    accountId?: string;
    docs: CaseDocument[];
    canEditDocs: boolean;
    canDeleteDocs: boolean;
    canUploadDocs: boolean;
    onDeleteDocs: (docIds: string[]) => Promise<void>;
    onModifyDocument: (doc: CaseDocument) => Promise<void>;
};

const DocumentsTab = (props: DocumentsTabProps) => {
    const { caseId, eventObj, accountId, docs, canEditDocs, canDeleteDocs, canUploadDocs, onDeleteDocs, onModifyDocument } =
        props;

    const toasts = useToasts();
    const tableFeatures = useCustomDataTableFeatures();
    const utils = useUtilities();
    const caseDocActions = useCaseDocumentActions();

    const fileUploadManager: FileUploadManager = FileUploadManager.getInstance(caseDocActions);

    const [selectedDocs, setSelectedDocs] = useState<CaseDocument[]>([]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [singleSelect, setSingleSelect] = useState<boolean>(false);

    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const btnClasses = mergeClasses(commonStyles.button.root, styles.documentsBtn);
    const uploadBtnClasses = mergeClasses(commonStyles.button.root, styles.documentsUploadBtn);

    const canUpload = () => {
        return canUploadDocs && !!caseId && !!eventObj.eventId;
    };
    const filterEventDocs = (doc: CaseDocument) => {
        return utils.lifecycle.filterNotDeleted(doc) && doc.eventId === eventObj.eventId;
    };
    const clearSelectedDocs = () => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
    };
    const deleteSelectedDocs = async (docsToDelete: CaseDocument[]) => {
        try {
            clearSelectedDocs();

            await onDeleteDocs(docsToDelete.map((d) => d.docId as string));

            if (docsToDelete.length > 1) {
                toasts.caseDocuments.notifyCaseDocsDeleted(docsToDelete);
            } else {
                toasts.caseDocuments.notifyCaseDocDeleted(docsToDelete[0]);
            }
        } catch {
            toasts.caseDocuments.notifyCaseDocDeleteError();
        }
    };
    const describeDropRejection = (rejection: FileRejection) => {
        const reason = rejection.errors
            .map((error) => {
                if (error.code === 'file-too-large') {
                    return `exceeds ${(MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB`;
                }
                if (error.code === 'file-invalid-type') {
                    return 'has an unsupported file type';
                }
                if (error.code === 'too-many-files') {
                    return `exceeds the ${MAX_FILES_PER_DROP} files per upload limit`;
                }

                return error.message;
            })
            .join(', ');

        return `${rejection.file.name}: ${reason}`;
    };

    const onSelectedDocsChanged = (selected: CaseDocument[]) => {
        setSelectedDocs(selected);
        setDrawerOpen(selected.length === 1);
    };
    const onSwitchChanged = (ev: any) => {
        ev.stopPropagation();
        setSingleSelect(ev.currentTarget.checked);
    };
    const onClickUpload = (e: any) => {
        e.stopPropagation();
        dropzone.open();
    };
    const onClickDetails = (e: any) => {
        e.stopPropagation();
        setDrawerOpen(true);
    };
    const onConfirmDelete = async (e: any) => {
        e.stopPropagation();
        await deleteSelectedDocs(selectedDocs);
    };
    const onUpdateDoc = async (doc: CaseDocument) => {
        try {
            await onModifyDocument({ ...doc, eventId: eventObj.eventId, caseId });
            toasts.caseDocuments.notifyCaseDocUpdated(doc);
        } catch {
            toasts.caseDocuments.notifyCaseDocUpdatedError(doc);
        }
    };

    const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (!eventObj.eventId) {
            toasts.notifyError('Upload unavailable', 'Please save this event before uploading documents.');
            return;
        }

        if (!caseId) {
            toasts.notifyError('Upload unavailable', 'Please select a case before uploading documents.');
            return;
        }

        if (acceptedFiles.length > 0) {
            fileUploadManager.setEventId(eventObj.eventId);
            fileUploadManager.queueFiles(caseId, acceptedFiles);
        }

        if (fileRejections.length > 0) {
            const rejectionMessages = fileRejections.slice(0, 3).map(describeDropRejection);
            const suffix = fileRejections.length > 3 ? ` (and ${fileRejections.length - 3} more)` : '';

            toasts.notifyWarning('Some files were not added', `${rejectionMessages.join(' | ')}${suffix}`);
        }
    };

    const dropzone = useDropzone({
        onDrop,
        accept: ACCEPTED_DOCUMENT_TYPES,
        maxFiles: MAX_FILES_PER_DROP,
        maxSize: MAX_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true,
    });

    useEffect(() => {
        if (accountId) {
            fileUploadManager.setAccount(accountId);
        }
    }, [accountId]);

    useEffect(() => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
        setDrawerOpen(false);
        // Only reset selection when switching events; tableFeatures reference is not stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventObj.eventId]);

    return (
        <Column flex='1' noGap>
            <Row padding='pl-3'>
                <Column>
                    <Row noGap>
                        <Column width='auto' vAlign='middle' height='auto'>
                            <Label>Selection mode</Label>
                        </Column>
                        <Column width='auto' vAlign='middle' height='auto'>
                            <Switch
                                label='Single-select'
                                checked={singleSelect}
                                onChange={onSwitchChanged}
                                data-testid='event-documents-single-switch'
                            />
                        </Column>
                    </Row>
                </Column>

                <Toolbar>
                    {(canEditDocs || canDeleteDocs) && !!selectedDocs.length && (
                        <>
                            <ToolbarDivider />

                            <Text>{selectedDocs.length} Documents selected</Text>

                            <ToolbarDivider />

                            {!!canEditDocs && selectedDocs.length === 1 && (
                                <Button
                                    className={btnClasses}
                                    aria-label='Details'
                                    appearance='subtle'
                                    shape='circular'
                                    icon={<InfoRegular />}
                                    onClick={onClickDetails}
                                    data-testid='event-documents-details-btn'
                                />
                            )}
                            {!!canDeleteDocs && (
                                <ConfirmationDialog
                                    title='Confirm Delete'
                                    icon={<DeleteRegular />}
                                    text='Are you sure you want to delete the selected documents?'
                                    onConfirm={onConfirmDelete}
                                >
                                    <Button
                                        className={btnClasses}
                                        aria-label='Delete'
                                        icon={<DeleteRegular />}
                                        appearance='subtle'
                                        shape='circular'
                                        data-testid='event-documents-delete-btn'
                                    />
                                </ConfirmationDialog>
                            )}
                        </>
                    )}
                </Toolbar>

                {canUpload() && (
                    <Column width='auto' padding='pr-3' vAlign='middle'>
                        <Tooltip content='Upload documents to this event' relationship='label'>
                            <Button
                                className={uploadBtnClasses}
                                onClick={onClickUpload}
                                icon={<ArrowUpload16Regular />}
                                data-testid='event-documents-upload-btn'
                            >
                                Upload
                            </Button>
                        </Tooltip>
                    </Column>
                )}
            </Row>

            <Column noGap flex='1' overflowX='hidden' overflowY='hidden' className={styles.documentsContentArea}>
                {canUpload() && (
                    <Column
                        flex='1'
                        align='center'
                        vAlign='middle'
                        {...dropzone.getRootProps()}
                        className={mergeClasses(styles.documentsDragArea, dropzone.isDragActive && styles.documentsDragActive)}
                        data-testid='event-documents-drop-zone'
                    >
                        <Text size={900}>Drop Files Here...</Text>
                        <Text size={200}>Supported files only, up to 100MB each and 20 files per upload.</Text>
                        <input {...dropzone.getInputProps()} />
                    </Column>
                )}

                <Row noWrap noGap flex='1'>
                    <DocumentTable
                        docs={docs.filter(filterEventDocs)}
                        singleSelect={singleSelect}
                        onSelectionChanged={onSelectedDocsChanged}
                        bodyHeight='calc(75vh - 22rem)'
                        onModifyDocument={onUpdateDoc}
                        onDeleteDocs={deleteSelectedDocs}
                        setDrawerOpen={setDrawerOpen}
                        {...tableFeatures.tableProps}
                    />
                    <DocumentDrawer
                        className={styles.documentsDrawer}
                        open={drawerOpen}
                        setOpen={setDrawerOpen}
                        position='end'
                        selectedDoc={docs.find((d) => d.docId == selectedDocs[0]?.docId)}
                        onSubmit={onUpdateDoc}
                    />
                </Row>
            </Column>
        </Column>
    );
};
